import { useState } from "react";

/**
 * One page of `T`, mirroring the server's `*Page` object types.
 *
 * `totalCount` is of everything matching the filter, not of `items` — which is
 * the whole reason the field exists. A queue heading reads "62 waiting" from it;
 * `items.length` would read "20".
 *
 * Shared rather than living in one feature: three features consume paged lists,
 * and the copy that gets written by hand is where `totalCount` quietly becomes
 * optional.
 */
export interface Paged<T> {
  items: T[];
  totalCount: number;
}

/** The page arguments a paginated query takes. Both optional; the server defaults. */
export interface PageVariables {
  limit?: number;
  offset?: number;
}

/**
 * The current page's offset, with the one correction offset paging always needs.
 *
 * ## The problem this exists for
 *
 * A reviewer on the last page of the queue publishes the rows there. The total
 * drops, that page no longer exists, and the next fetch returns an empty page
 * beside a non-zero total — a screen reading "62 waiting" above nothing at all.
 * The reviewer's only recourse is to guess that paging back will fix it.
 *
 * So when the offset lands past the end of a list that is not itself empty,
 * `reconcile` steps it back to the last page that has rows. Publishing the final
 * page of a queue then walks backwards through it rather than dead-ending.
 *
 * ## Why the total arrives through a method rather than an argument
 *
 * The offset is an *input* to the query and the total is an *output* of it, so
 * the hook cannot take the total as a parameter — that would be a cycle. It owns
 * the offset, and the caller hands the total back once the query has resolved:
 *
 * ```ts
 * const page = useOffset(PAGE_SIZE);
 * const { data } = useSuspenseQuery(queries.pending({ offset: page.offset }));
 * page.reconcile(data.eventPage.totalCount);
 * ```
 *
 * The correction is applied on the way out, not written back into state — so the
 * empty page is never rendered, only avoided. See the note in the body.
 */
/**
 * The offset a list should be showing, given where it is and how big it now is.
 *
 * Pure and exported so the recovery rule is testable without rendering — the
 * arithmetic is the part that is easy to get subtly wrong, and it is the part
 * that decides whether a reviewer who publishes the last page ends up on a real
 * page or a blank one.
 *
 * Returns `offset` unchanged in every case that needs no correction.
 *
 * @param offset Where the list currently is.
 * @param limit Page size.
 * @param total How many rows there are now. `null` while unknown.
 */
export function correctedOffset(
  offset: number,
  limit: number,
  total: number | null,
): number {
  /*
   * Nothing to correct against. `null` is "the first fetch has not resolved";
   * zero is a genuinely empty list, which must stay where it is and render its
   * own empty state — "nothing waiting for review" is the good news the queue
   * exists to deliver, not a condition to fix.
   */
  if (total === null || total === 0) return offset;

  /* In range, including a list that grew while someone was reading it. */
  if (offset < total) return offset;

  /*
   * The start of the last page that has rows. Floor-then-multiply rather than
   * `total - limit`, which would leave a partial page straddling the end and
   * re-show rows already seen on the previous page.
   */
  return Math.floor((total - 1) / limit) * limit;
}

export function useOffset(limit: number) {
  const [requested, setRequested] = useState(0);
  const [total, setTotal] = useState<number | null>(null);

  /*
   * The correction is **derived, not stored**.
   *
   * The obvious shape — an effect that notices the offset is out of range and
   * writes a corrected one back — sets state from an effect, which renders the
   * broken page once before fixing it. Here the offset a caller reads is already
   * corrected, so the empty page never renders at all.
   *
   * `requested` stays as the reviewer left it. That matters for the sync case: if
   * the queue shrinks and then grows again — rows published, then a sync stages
   * more — they return to the page they were on rather than to wherever the
   * correction had pushed them.
   */
  const offset = correctedOffset(requested, limit, total);

  return {
    offset,
    setOffset: setRequested,
    /**
     * Feed the server's `totalCount` back in.
     *
     * Safe to call during render, and expected to be: the total comes out of the
     * query whose offset this hook owns, so there is no earlier point at which it
     * is known.
     *
     * ## The guard has to be OUTSIDE the updater
     *
     * This was `setTotal((current) => (current === count ? current : count))`,
     * which reads like the same thing and is not. React's render-phase update
     * path re-runs the component body as soon as a setter is called during
     * render, and it decides whether to re-run by comparing against the value
     * the setter was *called with* — an updater function is always a new
     * function, so there is nothing to compare and the re-render always
     * happens. The next render calls `reconcile` again, and the loop only ends
     * when React gives up:
     *
     *   Too many re-renders. React limits the number of renders to prevent an
     *   infinite loop.
     *
     * Comparing `total !== count` here means the setter is not called at all
     * once the total is settled, so there is no render-phase update to react to.
     * Measured: `/events/mine` and `/admin/review` both threw on every load
     * before this, and both render in one pass after it.
     *
     * Do not "simplify" this back into an updater.
     */
    reconcile: (count: number) => {
      if (total !== count) setTotal(count);
    },
  };
}

/**
 * A 1-based page number from the URL → the 0-based offset a query wants.
 *
 * The URL carries the page because that is the number a person reads and edits;
 * the query wants an offset. This is the one place the two representations meet,
 * so `?page=3` and "rows 41–60" cannot drift apart.
 *
 * Anything absent or below 1 is the first page — a hand-mangled link should open
 * the listing rather than fail.
 */
export function toOffset(page: number | undefined, limit: number): number {
  if (page === undefined || page < 2) return 0;
  return (page - 1) * limit;
}

/** The 1-based page an offset falls on, for writing back to the URL. */
export function toPage(offset: number, limit: number): number {
  return Math.floor(offset / limit) + 1;
}
